/**
 * EmptyState — Shown when a view has no items.
 *
 * Supports filter-aware messaging (tag filters), icons from the icon registry,
 * and an optional CTA button.
 */

import { useTranslation } from "react-i18next"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"
import type { ReactNode } from "react"

export interface EmptyStateProps {
  title: string
  subtitle: string
  icon?: RegisteredIconType | null
  /** When non-empty, shows a filter-aware message instead of the default. */
  filterTags?: string[]
  /** Optional call-to-action button */
  cta?: ReactNode
}

export function EmptyState({
  title,
  subtitle,
  icon,
  filterTags,
  cta,
}: EmptyStateProps) {
  const { t } = useTranslation()
  const hasFilterTags = filterTags && filterTags.length > 0
  const isFiltered = hasFilterTags || !!cta
  // Use the custom title when provided with a CTA (e.g. "No matching projects"),
  // only override with "No matching tasks" when filter tags are the signal.
  const displayTitle = hasFilterTags ? t("No matching tasks") : t(title)
  const displaySubtitle = hasFilterTags
    ? t("No tasks match tag {{tags}}", {
        tags: filterTags.map((tag) => `#${tag}`).join(", "),
      })
    : isFiltered
      ? t("Try adjusting your search or filters")
      : t(subtitle)

  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {icon && (
        <div className="mb-3 opacity-30">
          <StaticIcon type={icon} size={32} />
        </div>
      )}
      <p className="text-base font-medium text-muted-foreground">
        {displayTitle}
      </p>
      <p className="mt-1 text-sm text-muted-foreground/60">{displaySubtitle}</p>
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  )
}
